<template>
  <wizard-form v-bind="ouiaProps" :hidden="hidden" :title="$t('Configurazione')">
    <pf-title h="2" class="pf-v6-u-mt-lg">{{ $t('Configurazione applicativa') }}</pf-title>

    <wizard-question v-if="wizard.askDb" :title="$t('Database')">
      <template #description>{{ $t('Indicaci quale database vuoi attivare.') }}</template>
      <wizard-question-options name="provision_db" v-model="wizard.db" :options="filteredOpzioniDb" required :null-label="$t('Non necessario')" />
    </wizard-question>

    <wizard-question v-if="wizard.askPhp" :title="$t('Versione PHP')">
      <template #description>{{ $t('Indicaci quale versione di PHP vuoi attivare.') }}</template>
      <wizard-question-options name="provision_php" v-model="wizard.php" :options="filteredOpzioniPhp" required :null-label="$t('Non necessario')" />
    </wizard-question>

    <wizard-question v-if="wizard.askTecnologie" :title="$t('Tecnologie e applicativi')">
      <template #description>{{ $t('Indicaci quali di queste tecnologie verranno utilizzate.') }}</template>
      <wizard-question-tecnologie v-model="wizard.tecnologie" />
    </wizard-question>
  </wizard-form>
</template>

<script lang="ts" setup>
import { $t as _t } from '@/i18n';
import { computed } from 'vue';
import { PRODOTTIORDINI_PROVISIONDB, PRODOTTIORDINI_PROVISIONPHP } from '@common/consts';
import { useOUIAProps, type OUIAProps } from '@common/ouia';
import { useWizardStep } from '@/wizard';

import WizardQuestion from '../questions/wizard-question.vue';
import WizardQuestionTecnologie from '../questions/wizard-question-tecnologie.vue';
import wizardQuestionOptions from '../questions/wizard-question-options.vue';
import WizardForm from '../wizard-form.vue';

defineOptions({
  name: 'WizardStepConfigurazione',
});

const props = defineProps<OUIAProps>();
const ouiaProps = useOUIAProps(props);

const { wizard, hidden, isEnabled } = useWizardStep({
  isEnabled: wizard => wizard.askConfigurazione,
});

defineExpose({
  isEnabled,
});

const opzioniPhp: Record<(typeof PRODOTTIORDINI_PROVISIONPHP)[number], string> = {
  best: _t('Migliore disponibile'),
  'v8.5': '8.5',
  'v8.4': '8.4',
  'v8.3': '8.3',
  'v8.2': '8.2',
  'v8.1': '8.1',
  'v8.0': '8.0',
  'v7.4': '7.4',
  'v7.3': '7.3',
  'v7.2': '7.2',
  'v7.1': '7.1',
  'v7.0': '7.0',
  'v5.6': '5.6',
  'v5.5': '5.5',
  'v5.4': '5.4',
  'v5.3': '5.3',
  'v5.2': '5.2',
};

const filteredOpzioniPhp = computed(() => {
  if (wizard.askPhp && wizard.stack === 'cockpit' && wizard.cockpitOs === 'AlmaLinux 9') {
    return Object.fromEntries(Object.entries(opzioniPhp).filter(([key]) => key >= 'v7.4' || key === 'best')) as typeof opzioniPhp;
  }
  if (wizard.askPhp && wizard.stack === 'cockpit' && wizard.cockpitOs === 'AlmaLinux 8') {
    return Object.fromEntries(Object.entries(opzioniPhp).filter(([key]) => key >= 'v5.4' || key === 'best')) as typeof opzioniPhp;
  }

  return opzioniPhp;
});

const opzioniDb: Record<(typeof PRODOTTIORDINI_PROVISIONDB)[number], string> = {
  best: _t('Migliore disponibile'),
  mariadb: 'MariaDB',
  'mysql-8': 'MySQL 8',
  'mysql-57': 'MySQL 5.7',
  'postgresql-11': 'PostgreSQL 11',
  'postgresql-12': 'PostgreSQL 12',
  'postgresql-13': 'PostgreSQL 13',
  'postgresql-14': 'PostgreSQL 14',
  'postgresql-15': 'PostgreSQL 15',
  'postgresql-16': 'PostgreSQL 16',
  'postgresql-17': 'PostgreSQL 17',
  mongodb: 'Mongo DB',
};

const filteredOpzioniDb = computed(() => {
  if (wizard.sceltaProdottoInfrastruttura === 'HOSTITA') {
    return Object.fromEntries(Object.entries(opzioniDb).filter(([key]) => key === 'mariadb')) as typeof opzioniDb;
  }

  if (wizard.onHostingPath) {
    return Object.fromEntries(Object.entries(opzioniDb).filter(([key]) => key !== 'mongodb' && !key.startsWith('postgresql-'))) as typeof opzioniDb;
  }

  // Se ci sono DB di tecnologie diverse e non solo MySQL l'opzione 'best' perde di senso
  return Object.fromEntries(Object.entries(opzioniDb).filter(([key]) => key !== 'best')) as typeof opzioniDb;
});
</script>
